import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Tab, Tabs, Alert, Spinner } from 'react-bootstrap';
import { FaUser, FaUserPlus, FaExchangeAlt, FaHistory, FaUndo, FaBuilding, FaEdit, FaCheckCircle, FaFingerprint } from 'react-icons/fa';
import api from '../services/api';

const DMTBank6Modal = ({ show, onHide, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('merchant');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');

  // Merchant States (only required fields)
  const [merchantData, setMerchantData] = useState({
    mobile: '',
    name: '',
    firmname: '',
    shopaddress: '',
    dob: '',
    fathername: '',
    email: '',
    pincode: '',
    address: '',
    city: '',
    state: '',
    pannumber: '',
    aadhaarnumber: '', // Required by PaySprint
    pid_data: '' // Optional: Can be captured via fingerprint
  });

  // Fingerprint capture states
  const [fingerprintStatus, setFingerprintStatus] = useState('idle'); // idle, capturing, captured, verified, failed
  const [pidData, setPidData] = useState('');

  // PAN Update States
  const [panUpdateData, setPanUpdateData] = useState({
    pannumber: '',
    name: ''
  });

  // Merchant Status States
  const [statusData, setStatusData] = useState({
    merchantcode: ''
  });

  // Remitter States (with all required fields for PaySprint)
  const [remitterData, setRemitterData] = useState({
    mobile: '',
    name: '',
    aadhaar: '', // Changed from aadhar to aadhaar (PaySprint expects this)
    address: '',
    pincode: '',
    city: '',
    state: '',
    otp: '', // Required by PaySprint
    pid_data: '', // Required - encrypted fingerprint data
    latitude: '', // Required (will use default if not provided)
    longitude: '', // Required (will use default if not provided)
    accessmode: 'SITE' // Required: SITE or APP
  });
  
  // Remitter fingerprint capture state
  const [remitterFingerprintStatus, setRemitterFingerprintStatus] = useState('idle');

  // Beneficiary States
  const [beneficiaryData, setBeneficiaryData] = useState({
    mobile: '',
    bene_name: '',
    bene_account: '',
    bene_ifsc: '',
    bene_bank: '',
    bankid: '',
    verified: '0'
  });

  // Transaction States
  const DEFAULT_LAT = '28.644800';
  const DEFAULT_LONG = '77.216721';

  const [transactionData, setTransactionData] = useState({
    mobile: '',
    beneid: '',
    amount: '',
    otp: '',
    referenceid: '',
    txntype: 'NEFT',
    latitude: DEFAULT_LAT,
    longitude: DEFAULT_LONG,
    stateresp: ''
  });

  // Refund States
  const [refundData, setRefundData] = useState({
    transactionid: '',
    otp: ''
  });

  // Lists
  const [beneficiaries, setBeneficiaries] = useState([]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      setActiveTab('merchant');
      setMessage('');
      setMessageType('info');
      setMerchantData({
        mobile: '',
        name: '',
        firmname: '',
        shopaddress: '',
        dob: '',
        fathername: '',
        email: '',
        pincode: '',
        address: '',
        city: '',
        state: '',
        pannumber: '',
        aadhaarnumber: '',
        pid_data: ''
      });
      setFingerprintStatus('idle');
      setPidData('');
      setPanUpdateData({
        pannumber: '',
        name: ''
      });
      setStatusData({
        merchantcode: ''
      });
      setRemitterData({
        mobile: '',
        name: '',
        aadhaar: '',
        address: '',
        pincode: '',
        city: '',
        state: '',
        otp: '',
        pid_data: '',
        latitude: '',
        longitude: '',
        accessmode: 'SITE'
      });
      setRemitterFingerprintStatus('idle');
      setBeneficiaryData({
        mobile: '',
        bene_name: '',
        bene_account: '',
        bene_ifsc: '',
        bene_bank: '',
        bankid: '',
        verified: '0'
      });
      setTransactionData({
        mobile: '',
        beneid: '',
        amount: '',
        otp: '',
        referenceid: '',
        txntype: 'NEFT',
        latitude: DEFAULT_LAT,
        longitude: DEFAULT_LONG,
        stateresp: ''
      });
      setRefundData({
        transactionid: '',
        otp: ''
      });
      setBeneficiaries([]);
    }
  }, [show]);

  const showMessage = (msg, type = 'info') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  // Helper function to format PAN number (uppercase, alphanumeric only, max 10 chars)
  const formatPanNumber = (value) => {
    // Remove any non-alphanumeric characters and convert to uppercase
    const cleaned = value.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    // Limit to 10 characters
    return cleaned.slice(0, 10);
  };

  // Helper function to clean city name (remove numbers and spaces, keep only alphabets)
  // PaySprint doesn't accept spaces in city name
  const cleanCityName = (value) => {
    // Remove numbers, special characters, and spaces - keep only alphabets
    return value.replace(/[^a-zA-Z]/g, '');
  };

  const generateReferenceId = () => {
    const timestamp = Date.now().toString();
    const randomSuffix = Math.floor(Math.random() * 9000 + 1000).toString();
    return `BT${timestamp}${randomSuffix}`;
  };

  // Capture fingerprint from IDEMIA L1 RD Service (port 11100, /capture)
  const captureFingerprint = async () => {
    setFingerprintStatus('capturing');
    showMessage('Please place your finger on the biometric device...', 'info');

    try {
      const { captureFingerprint: rdCapture } = await import('../utils/rdService');
      const result = await rdCapture();

      if (result.success) {
        const pidDataXml = result.pidData;
        const qualityScore = result.qualityScore;
        if (qualityScore == null || qualityScore >= 50) {
          setPidData(pidDataXml);
          setMerchantData({...merchantData, pid_data: pidDataXml});
          setFingerprintStatus('captured');
          showMessage(`Fingerprint captured successfully (Quality: ${qualityScore ?? 'N/A'}%). PID Data auto-filled.`, 'success');
          await new Promise(resolve => setTimeout(resolve, 1000));
          setFingerprintStatus('verified');
          showMessage('Fingerprint verified successfully', 'success');
        } else {
          setFingerprintStatus('failed');
          showMessage(`Fingerprint quality too low (${qualityScore}%). Please try again with a clean finger.`, 'danger');
        }
      } else {
        setFingerprintStatus('failed');
        showMessage(`Fingerprint capture failed (Error ${result.errorCode}: ${result.errorInfo}). Please try again.`, 'danger');
      }
    } catch (error) {
      setFingerprintStatus('failed');
      showMessage(error.message || 'Fingerprint capture failed. Ensure IDEMIA L1 RD Service is running (port 11100).', 'danger');
      console.error('Fingerprint capture error:', error);
    }
  };

  // Merchant Functions
  const merchantRegistration = async () => {
    // Check all required fields
    const missingFields = [];
    if (!merchantData.mobile) missingFields.push('Mobile');
    if (!merchantData.name) missingFields.push('Name');
    if (!merchantData.firmname) missingFields.push('Firm Name');
    if (!merchantData.shopaddress) missingFields.push('Shop Address');
    if (!merchantData.dob) missingFields.push('Date of Birth');
    if (!merchantData.fathername) missingFields.push('Father Name');
    if (!merchantData.email) missingFields.push('Email');
    if (!merchantData.pincode) missingFields.push('Pincode');
    if (!merchantData.address) missingFields.push('Address');
    if (!merchantData.city) missingFields.push('City');
    if (!merchantData.state) missingFields.push('State');
    if (!merchantData.pannumber) missingFields.push('PAN Number');
    if (!merchantData.aadhaarnumber) missingFields.push('Aadhaar Number');
    if (!merchantData.pid_data || merchantData.pid_data.trim() === '') {
      missingFields.push('PID Data (Please capture fingerprint)');
    }

    if (missingFields.length > 0) {
      showMessage(`Please fill all required fields: ${missingFields.join(', ')}`, 'danger');
      return;
    }

    setLoading(true);
    try {
      // Prepare data - ensure all fields are included
      const requestData = { ...merchantData };
      
      console.log('Sending merchant registration request:', {
        ...requestData,
        pid_data: requestData.pid_data ? 'Present (hidden)' : 'Not provided',
        aadhaarnumber: requestData.aadhaarnumber ? `${requestData.aadhaarnumber.substring(0, 4)}XXXX${requestData.aadhaarnumber.substring(8)}` : 'MISSING'
      });

      const response = await api.post('/api/dmt-bank6/merchant/register', requestData);

      if (response.data.success) {
        showMessage('Merchant registration successful', 'success');
        if (onSuccess) {
          onSuccess(response.data.data);
        }
      } else {
        showMessage(response.data.message || 'Merchant registration failed', 'danger');
      }
    } catch (error) {
      console.error('Merchant Registration Error:', error);
      
      // Show detailed error message
      let errorMessage = 'Failed to register merchant';
      if (error.response) {
        // Server responded with error
        if (error.response.data) {
          if (error.response.data.errors) {
            // Validation errors
            const errors = error.response.data.errors;
            const errorList = Object.keys(errors).map(key => `${key}: ${errors[key][0]}`).join(', ');
            errorMessage = `Validation failed: ${errorList}`;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          }
        }
        console.error('Error response:', error.response.data);
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your connection.';
      }
      
      showMessage(errorMessage, 'danger');
    } finally {
      setLoading(false);
    }
  };

  const merchantPanUpdate = async () => {
    if (!panUpdateData.pannumber || !panUpdateData.name) {
      showMessage('Please fill all required fields', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-bank6/merchant/pan-update', panUpdateData);

      if (response.data.success) {
        showMessage('PAN name updated successfully', 'success');
      } else {
        showMessage(response.data.message || 'PAN name update failed', 'danger');
      }
    } catch (error) {
      console.error('PAN Update Error:', error);
      showMessage('Failed to update PAN name', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const merchantStatusCheck = async () => {
    if (!statusData.merchantcode) {
      showMessage('Please enter merchant code', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-bank6/merchant/status-check', statusData);

      if (response.data.success) {
        showMessage('Merchant status retrieved successfully', 'success');
      } else {
        showMessage(response.data.message || 'Failed to get merchant status', 'danger');
      }
    } catch (error) {
      console.error('Merchant Status Check Error:', error);
      showMessage('Failed to check merchant status', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Remitter Functions
  const queryRemitter = async () => {
    if (!remitterData.mobile) {
      showMessage('Please enter mobile number', 'danger');
      return;
    }

    setLoading(true);
    try {
      // PaySprint requires latitude, longitude, and accessmode for Query Remitter
      const payload = {
        mobile: remitterData.mobile,
        latitude: remitterData.latitude || '28.6139', // Default: Delhi
        longitude: remitterData.longitude || '77.2090', // Default: Delhi
        accessmode: (remitterData.accessmode || 'SITE').toUpperCase()
      };
      
      const response = await api.post('/api/dmt-bank6/remitter/query', payload);

      if (response.data.success) {
        if (response.data.data?.response_code === 1) {
          showMessage('Remitter found! You can proceed to add beneficiaries and make transactions.', 'success');
          setActiveTab('beneficiary'); // Move to beneficiary tab
          await fetchBeneficiaries();
        } else if (response.data.data?.response_code === 2) {
          showMessage('Please complete registration process.', 'warning');
          setActiveTab('remitter'); // Stay on remitter tab for registration
        } else {
          showMessage('Remitter not found. Please complete registration process.', 'warning');
          setActiveTab('remitter'); // Stay on remitter tab for registration
        }
      } else {
        showMessage(response.data.message || 'Remitter query failed', 'danger');
      }
    } catch (error) {
      console.error('Query Remitter Error:', error);
      showMessage('Failed to query remitter', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Beneficiaries
  const fetchBeneficiaries = async () => {
    try {
      const response = await api.post('/api/dmt-bank6/beneficiary/fetch', {
        mobile: remitterData.mobile
      });

      if (response.data.success) {
      const list = response.data.data?.beneficiaries || response.data.data || [];
      const normalized = list.map((item) => ({
        beneid: item.beneid || item.bene_id || item.id,
        bene_name: item.bene_name || item.name || '',
        bene_account: item.bene_account || item.accno || '',
        bene_ifsc: item.bene_ifsc || item.ifsc || '',
        bene_bank: item.bene_bank || item.bankname || '',
        bankid: item.bankid || '',
        verified: item.verified ?? '0',
      }));
      setBeneficiaries(normalized);
      }
    } catch (err) {
      console.error('Failed to fetch beneficiaries:', err);
    }
  };

  // Capture fingerprint for remitter (IDEMIA L1 RD Service)
  const captureRemitterFingerprint = async () => {
    setRemitterFingerprintStatus('capturing');
    showMessage('Please place your finger on the biometric device...', 'info');

    try {
      const { captureFingerprint: rdCapture } = await import('../utils/rdService');
      const result = await rdCapture();

      if (result.success) {
        const pidDataXml = result.pidData;
        const qualityScore = result.qualityScore;
        if (qualityScore == null || qualityScore >= 50) {
          setRemitterData({...remitterData, pid_data: pidDataXml});
          setRemitterFingerprintStatus('captured');
          showMessage(`Fingerprint captured successfully (Quality: ${qualityScore ?? 'N/A'}%)`, 'success');
          
          await new Promise(resolve => setTimeout(resolve, 1000));
          setRemitterFingerprintStatus('verified');
        } else {
          setRemitterFingerprintStatus('failed');
          showMessage(`Fingerprint quality too low (${qualityScore}%). Please try again.`, 'danger');
        }
      } else {
        setRemitterFingerprintStatus('failed');
        showMessage(`Fingerprint capture failed (Error ${result.errorCode}: ${result.errorInfo}). Please try again.`, 'danger');
      }
    } catch (error) {
      console.error('Fingerprint Capture Error:', error);
      setRemitterFingerprintStatus('failed');
      showMessage(error.message || 'Fingerprint capture failed. Ensure IDEMIA L1 RD Service is running (port 11100).', 'danger');
    }
  };

  const registerRemitter = async () => {
    // Validate required fields
    if (!remitterData.mobile || !remitterData.name || !remitterData.aadhaar || 
        !remitterData.address || !remitterData.pincode || !remitterData.city || 
        !remitterData.state || !remitterData.otp || !remitterData.pid_data) {
      showMessage('Please fill all required fields including OTP and capture fingerprint', 'danger');
      return;
    }

    // Validate Aadhaar (12 digits)
    if (remitterData.aadhaar.length !== 12) {
      showMessage('Aadhaar number must be 12 digits', 'danger');
      return;
    }

    // Validate OTP (exactly 6 digits)
    if (remitterData.otp.length !== 6) {
      showMessage('OTP must be exactly 6 digits', 'danger');
      return;
    }

    setLoading(true);
    try {
      // Prepare payload with all required fields
      const payload = {
        ...remitterData,
        // Clean city name (remove numbers and spaces)
        city: cleanCityName(remitterData.city),
        // Ensure accessmode is uppercase
        accessmode: (remitterData.accessmode || 'SITE').toUpperCase()
      };

      console.log('Sending remitter registration request:', payload);
      const response = await api.post('/api/dmt-bank6/remitter/register', payload);

      if (response.data.success) {
        showMessage('Remitter registered successfully', 'success');
        if (onSuccess) {
          onSuccess(response.data.data);
        }
      } else {
        const errorMsg = response.data.message || response.data.error || 'Remitter registration failed';
        showMessage(errorMsg, 'danger');
      }
    } catch (error) {
      console.error('Register Remitter Error:', error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to register remitter';
      showMessage(errorMsg, 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Beneficiary Functions
  const registerBeneficiary = async () => {
    if (!beneficiaryData.mobile || !beneficiaryData.bene_name || !beneficiaryData.bene_account || !beneficiaryData.bene_ifsc || !beneficiaryData.bene_bank) {
      showMessage('Please fill all required fields', 'danger');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...beneficiaryData,
        bankid: beneficiaryData.bankid || beneficiaryData.bene_bank,
        verified: beneficiaryData.verified || '0'
      };
      const response = await api.post('/api/dmt-bank6/beneficiary/register', payload);

      if (response.data.success) {
        showMessage('Beneficiary registered successfully', 'success');
        setBeneficiaryData({
          mobile: '',
          bene_name: '',
          bene_account: '',
          bene_ifsc: '',
          bene_bank: '',
          verified: '0'
        });
        getBeneficiaries();
      } else {
        showMessage(response.data.message || 'Beneficiary registration failed', 'danger');
      }
    } catch (error) {
      console.error('Register Beneficiary Error:', error);
      const errorMsg =
        error.response?.data?.message
        || (error.response?.data?.errors && JSON.stringify(error.response.data.errors))
        || error.message
        || 'Failed to register beneficiary';
      showMessage(errorMsg, 'danger');
    } finally {
      setLoading(false);
    }
  };

  const getBeneficiaries = async () => {
    if (!beneficiaryData.mobile) {
      showMessage('Please enter mobile number', 'danger');
      return;
    }

    setBeneficiaries([]);
    setLoading(true);
    try {
      const response = await api.post('/api/dmt-bank6/beneficiary/fetch', {
        mobile: beneficiaryData.mobile
      });

      if (response.data.success) {
        const rawData = response.data.data ?? response.data.response?.data ?? [];
        const list = Array.isArray(rawData?.beneficiaries)
          ? rawData.beneficiaries
          : Array.isArray(rawData?.data)
            ? rawData.data
            : Array.isArray(rawData)
              ? rawData
              : [];

        const normalized = list.map((item) => ({
          beneid: item.beneid || item.bene_id || item.id || '',
          bene_name: item.bene_name || item.name || '',
          bene_account: item.bene_account || item.accno || '',
          bene_ifsc: item.bene_ifsc || item.ifsc || '',
          bene_bank: item.bene_bank || item.bankname || '',
          bankid: item.bankid || '',
          verified: item.verified ?? '0',
        }));

        setBeneficiaries(normalized);
        showMessage('Beneficiaries loaded successfully', 'success');
      } else {
        showMessage(response.data.message || 'Failed to get beneficiaries', 'danger');
        setBeneficiaries([]);
      }
    } catch (error) {
      console.error('Get Beneficiaries Error:', error);
      showMessage('Failed to get beneficiaries', 'danger');
      setBeneficiaries([]);
    } finally {
      setLoading(false);
    }
  };

  const verifyBeneficiary = async (beneId) => {
    setLoading(true);
    try {
      const response = await api.post('/api/dmt-bank6/beneficiary/verify', {
        beneid: beneId,
        bene_name: beneficiaryData.bene_name,
        bene_account: beneficiaryData.bene_account,
        bene_ifsc: beneficiaryData.bene_ifsc
      });

      if (response.data.success) {
        showMessage('Beneficiary verification completed', 'success');
      } else {
        showMessage(response.data.message || 'Beneficiary verification failed', 'danger');
      }
    } catch (error) {
      console.error('Verify Beneficiary Error:', error);
      showMessage('Failed to verify beneficiary', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const deleteBeneficiary = async (beneId) => {
    if (!window.confirm('Are you sure you want to delete this beneficiary?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-bank6/beneficiary/delete', {
        beneid: beneId
      });

      if (response.data.success) {
        showMessage('Beneficiary deleted successfully', 'success');
        getBeneficiaries();
      } else {
        showMessage(response.data.message || 'Failed to delete beneficiary', 'danger');
      }
    } catch (error) {
      console.error('Delete Beneficiary Error:', error);
      showMessage('Failed to delete beneficiary', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Transaction Functions
  const sendTransactionOtp = async () => {
    if (!transactionData.mobile || !transactionData.beneid || !transactionData.amount) {
      showMessage('Please fill all required fields', 'danger');
      return;
    }

    const amountValue = parseFloat(transactionData.amount);
    if (isNaN(amountValue) || amountValue <= 0) {
      showMessage('Please enter a valid amount greater than 0', 'danger');
      return;
    }

    const referenceId = transactionData.referenceid?.trim() || generateReferenceId();
    const txntype = transactionData.txntype || 'NEFT';
    const latitude = transactionData.latitude || DEFAULT_LAT;
    const longitude = transactionData.longitude || DEFAULT_LONG;

    setLoading(true);
    try {
      const payload = {
        mobile: transactionData.mobile.trim(),
        beneid: transactionData.beneid.trim(),
        bene_id: transactionData.beneid.trim(),
        amount: amountValue,
        referenceid: referenceId,
        txntype,
        latitude,
        longitude
      };

      const response = await api.post('/api/dmt-bank6/transaction/send-otp', payload);

      if (response.data.success) {
        const stateresp =
          response.data?.data?.stateresp ||
          response.data?.data?.state_resp ||
          response.data?.data?.otp_reference_number ||
          response.data?.data?.data?.stateresp ||
          response.data?.data?.data?.state_resp ||
          response.data?.data?.data?.otp_reference_number ||
          response.data?.stateresp ||
          response.data?.state_resp ||
          response.data?.otp_reference_number ||
          '';

        setTransactionData((prev) => ({
          ...prev,
          referenceid: referenceId,
          txntype,
          latitude,
          longitude,
          stateresp: stateresp || prev.stateresp
        }));

        showMessage(
          `Transaction OTP sent successfully${
            stateresp ? ` (State Resp: ${stateresp})` : ''
          }`,
          'success'
        );
      } else {
        showMessage(response.data.message || 'Failed to send transaction OTP', 'danger');
      }
    } catch (error) {
      console.error('Send Transaction OTP Error:', error);
      const serverMessage =
        error?.response?.data?.message || error?.response?.data?.error || error.message;
      showMessage(serverMessage || 'Failed to send transaction OTP', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const processTransaction = async () => {
    if (
      !transactionData.mobile ||
      !transactionData.beneid ||
      !transactionData.amount ||
      !transactionData.otp
    ) {
      showMessage('Please fill all required fields', 'danger');
      return;
    }

    if (!transactionData.referenceid) {
      showMessage('Reference ID missing. Please send OTP again.', 'danger');
      return;
    }

    if (!transactionData.stateresp) {
      showMessage('OTP reference number missing. Please send OTP again.', 'danger');
      return;
    }

    const amountValue = parseFloat(transactionData.amount);
    if (isNaN(amountValue) || amountValue <= 0) {
      showMessage('Please enter a valid amount greater than 0', 'danger');
      return;
    }

    setLoading(true);
    try {
      const otpReference = transactionData.stateresp?.trim() || '';

      const payload = {
        mobile: transactionData.mobile.trim(),
        beneid: transactionData.beneid.trim(),
        bene_id: transactionData.beneid.trim(),
        amount: amountValue,
        otp: transactionData.otp.trim(),
        referenceid: transactionData.referenceid,
        txntype: transactionData.txntype || 'NEFT',
        latitude: transactionData.latitude || DEFAULT_LAT,
        longitude: transactionData.longitude || DEFAULT_LONG,
        stateresp: otpReference || undefined,
        otp_reference_number: otpReference || undefined
      };

      const response = await api.post('/api/dmt-bank6/transaction/process', payload);

      if (response.data.success) {
        showMessage('Transaction completed successfully', 'success');
        if (onSuccess) {
          onSuccess(response.data.data);
        }
        setTransactionData((prev) => ({
          ...prev,
          otp: '',
          stateresp: ''
        }));
      } else {
        showMessage(response.data.message || 'Transaction failed', 'danger');
      }
    } catch (error) {
      console.error('Process Transaction Error:', error);
      const serverMessage =
        error?.response?.data?.message || error?.response?.data?.error || error.message;
      showMessage(serverMessage || 'Failed to complete transaction', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const checkTransactionStatus = async () => {
    if (!refundData.transactionid) {
      showMessage('Please enter transaction ID', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-bank6/transaction/status', {
        transactionid: refundData.transactionid
      });

      if (response.data.success) {
        showMessage('Transaction status retrieved successfully', 'success');
      } else {
        showMessage(response.data.message || 'Failed to get transaction status', 'danger');
      }
    } catch (error) {
      console.error('Check Transaction Status Error:', error);
      showMessage('Failed to check transaction status', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Refund Functions
  const sendRefundOtp = async () => {
    if (!refundData.transactionid) {
      showMessage('Please enter transaction ID', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-bank6/refund/send-otp', {
        transactionid: refundData.transactionid
      });

      if (response.data.success) {
        showMessage('Refund OTP sent successfully', 'success');
      } else {
        showMessage(response.data.message || 'Failed to send refund OTP', 'danger');
      }
    } catch (error) {
      console.error('Send Refund OTP Error:', error);
      showMessage('Failed to send refund OTP', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const claimRefund = async () => {
    if (!refundData.transactionid || !refundData.otp) {
      showMessage('Please enter transaction ID and OTP', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/dmt-bank6/refund/claim', {
        transactionid: refundData.transactionid,
        otp: refundData.otp
      });

      if (response.data.success) {
        showMessage('Refund claimed successfully', 'success');
        if (onSuccess) {
          onSuccess(response.data.data);
        }
      } else {
        showMessage(response.data.message || 'Refund claim failed', 'danger');
      }
    } catch (error) {
      console.error('Claim Refund Error:', error);
      showMessage('Failed to claim refund', 'danger');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaBuilding className="me-2" />
          DMT Bank6 - Complete Money Transfer System
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {message && (
          <Alert variant={messageType} className="mb-3">
            {message}
          </Alert>
        )}

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
        >
          {/* Merchant Tab */}
          <Tab eventKey="merchant" title="Merchant">
            <div className="p-3">
              <h5><FaBuilding className="me-2" />Merchant Management</h5>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Mobile Number *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 10-digit mobile number"
                      value={merchantData.mobile}
                      onChange={(e) => setMerchantData({...merchantData, mobile: e.target.value})}
                      maxLength={10}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Name *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter full name"
                      value={merchantData.name}
                      onChange={(e) => setMerchantData({...merchantData, name: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Firm Name *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter firm name"
                      value={merchantData.firmname}
                      onChange={(e) => setMerchantData({...merchantData, firmname: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Shop Address *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Enter shop address"
                      value={merchantData.shopaddress}
                      onChange={(e) => setMerchantData({...merchantData, shopaddress: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Date of Birth *</Form.Label>
                    <Form.Control
                      type="date"
                      value={merchantData.dob}
                      onChange={(e) => setMerchantData({...merchantData, dob: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Father Name *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter father name"
                      value={merchantData.fathername}
                      onChange={(e) => setMerchantData({...merchantData, fathername: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Email *</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter email address"
                      value={merchantData.email}
                      onChange={(e) => setMerchantData({...merchantData, email: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Pincode *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 6-digit pincode"
                      value={merchantData.pincode}
                      onChange={(e) => setMerchantData({...merchantData, pincode: e.target.value})}
                      maxLength={6}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Address *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Enter complete address"
                      value={merchantData.address}
                      onChange={(e) => setMerchantData({...merchantData, address: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>City *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter city (alphabets only)"
                      value={merchantData.city}
                      onChange={(e) => setMerchantData({...merchantData, city: cleanCityName(e.target.value)})}
                    />
                    <Form.Text className="text-muted">
                      Only alphabets allowed (numbers and spaces will be removed automatically)
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>State *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter state"
                      value={merchantData.state}
                      onChange={(e) => setMerchantData({...merchantData, state: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>PAN Number *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter PAN number (e.g., ABCDE1234F)"
                      value={merchantData.pannumber}
                      onChange={(e) => setMerchantData({...merchantData, pannumber: formatPanNumber(e.target.value)})}
                      maxLength={10}
                      pattern="[A-Z0-9]{10}"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Aadhaar Number *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 12-digit Aadhaar number"
                      value={merchantData.aadhaarnumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').substring(0, 12);
                        setMerchantData({...merchantData, aadhaarnumber: value});
                      }}
                      maxLength={12}
                      pattern="[0-9]{12}"
                    />
                    <Form.Text className="text-muted">
                      Required by PaySprint for merchant registration
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>
                      PID Data (Encrypted) - Optional
                      {fingerprintStatus === 'verified' && (
                        <span className="text-success ms-2">
                          <FaCheckCircle /> Captured
                        </span>
                      )}
                    </Form.Label>
                    <div className="d-flex gap-2">
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="PID Data will be auto-filled when you capture fingerprint, or paste manually"
                        value={merchantData.pid_data}
                        onChange={(e) => {
                          setMerchantData({...merchantData, pid_data: e.target.value});
                          if (e.target.value) {
                            setPidData(e.target.value);
                            setFingerprintStatus('captured');
                          }
                        }}
                        readOnly={fingerprintStatus === 'capturing'}
                      />
                      <Button
                        variant={fingerprintStatus === 'verified' ? 'success' : fingerprintStatus === 'capturing' ? 'warning' : 'primary'}
                        onClick={captureFingerprint}
                        disabled={loading || fingerprintStatus === 'capturing'}
                        style={{ minWidth: '150px' }}
                      >
                        {fingerprintStatus === 'capturing' ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Capturing...
                          </>
                        ) : fingerprintStatus === 'verified' ? (
                          <>
                            <FaCheckCircle className="me-2" />
                            Re-capture
                          </>
                        ) : (
                          <>
                            <FaFingerprint className="me-2" />
                            Capture Fingerprint
                          </>
                        )}
                      </Button>
                    </div>
                    <Form.Text className="text-muted">
                      Click "Capture Fingerprint" to automatically capture PID Data from RD device (FM220U), or paste manually.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Button 
                variant="primary" 
                onClick={merchantRegistration}
                disabled={loading}
                className="w-100"
              >
                {loading ? <Spinner size="sm" /> : 'Register Merchant'}
              </Button>
            </div>
          </Tab>

          {/* PAN Update Tab */}
          <Tab eventKey="pan-update" title="PAN Update">
            <div className="p-3">
              <h5><FaEdit className="me-2" />PAN Name Update</h5>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>PAN Number *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter PAN number (e.g., ABCDE1234F)"
                      value={panUpdateData.pannumber}
                      onChange={(e) => setPanUpdateData({...panUpdateData, pannumber: formatPanNumber(e.target.value)})}
                      maxLength={10}
                      pattern="[A-Z0-9]{10}"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Name *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter updated name"
                      value={panUpdateData.name}
                      onChange={(e) => setPanUpdateData({...panUpdateData, name: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Button 
                variant="warning" 
                onClick={merchantPanUpdate}
                disabled={loading}
                className="w-100"
              >
                {loading ? <Spinner size="sm" /> : 'Update PAN Name'}
              </Button>
            </div>
          </Tab>

          {/* Status Check Tab */}
          <Tab eventKey="status-check" title="Status Check">
            <div className="p-3">
              <h5><FaCheckCircle className="me-2" />Merchant Status Check</h5>
              
              <Row className="mb-3">
                <Col md={8}>
                  <Form.Group>
                    <Form.Label>Merchant Code *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter merchant code"
                      value={statusData.merchantcode}
                      onChange={(e) => setStatusData({...statusData, merchantcode: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={4} className="d-flex align-items-end">
                  <Button 
                    variant="info" 
                    onClick={merchantStatusCheck}
                    disabled={loading}
                    className="w-100"
                  >
                    {loading ? <Spinner size="sm" /> : 'Check Status'}
                  </Button>
                </Col>
              </Row>
            </div>
          </Tab>

          {/* Remitter Tab */}
          <Tab eventKey="remitter" title="Remitter">
            <div className="p-3">
              <h5><FaUser className="me-2" />Remitter Management</h5>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Mobile Number</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 10-digit mobile number"
                      value={remitterData.mobile}
                      onChange={(e) => setRemitterData({...remitterData, mobile: e.target.value})}
                      maxLength={10}
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="d-flex align-items-end">
                  <Button 
                    variant="info" 
                    onClick={queryRemitter}
                    disabled={loading}
                    className="w-100"
                  >
                    {loading ? <Spinner size="sm" /> : 'Query Remitter'}
                  </Button>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Name *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter full name"
                      value={remitterData.name}
                      onChange={(e) => setRemitterData({...remitterData, name: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Aadhaar Number *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 12-digit Aadhaar number"
                      value={remitterData.aadhaar}
                      onChange={(e) => setRemitterData({...remitterData, aadhaar: e.target.value.replace(/\D/g, '').slice(0, 12)})}
                      maxLength={12}
                    />
                    <Form.Text className="text-muted">
                      12-digit Aadhaar number required
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Pincode *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 6-digit pincode"
                      value={remitterData.pincode}
                      onChange={(e) => setRemitterData({...remitterData, pincode: e.target.value})}
                      maxLength={6}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Address *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Enter complete address"
                      value={remitterData.address}
                      onChange={(e) => setRemitterData({...remitterData, address: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>City *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter city (alphabets only)"
                      value={remitterData.city}
                      onChange={(e) => setRemitterData({...remitterData, city: cleanCityName(e.target.value)})}
                    />
                    <Form.Text className="text-muted">
                      Only alphabets allowed (numbers and spaces will be removed automatically)
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>State *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter state"
                      value={remitterData.state}
                      onChange={(e) => setRemitterData({...remitterData, state: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Access Mode *</Form.Label>
                    <Form.Select
                      value={remitterData.accessmode}
                      onChange={(e) => setRemitterData({...remitterData, accessmode: e.target.value})}
                    >
                      <option value="SITE">SITE</option>
                      <option value="APP">APP</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Select access mode (SITE or APP)
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>OTP *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      value={remitterData.otp}
                      onChange={(e) => {
                        // Only allow digits and limit to 6 characters
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setRemitterData({...remitterData, otp: value});
                      }}
                      maxLength={6}
                      pattern="[0-9]{6}"
                    />
                    <Form.Text className="text-muted">
                      OTP must be exactly 6 digits
                      {remitterData.otp.length > 0 && remitterData.otp.length !== 6 && (
                        <span className="text-danger ms-2">({remitterData.otp.length}/6 characters)</span>
                      )}
                      {remitterData.otp.length === 6 && (
                        <span className="text-success ms-2">✓ Valid</span>
                      )}
                      <br />
                      <small className="text-info">
                        💡 UAT Testing: Use <strong>999999</strong> or <strong>123456</strong> (6 digits)
                      </small>
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>PID Data (Encrypted) *</Form.Label>
                    <div className="d-flex gap-2">
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="PID Data will be auto-filled after fingerprint capture"
                        value={remitterData.pid_data ? 'PID Data captured ✓' : ''}
                        readOnly
                        className={remitterData.pid_data ? 'border-success' : ''}
                      />
                      <Button
                        variant={remitterFingerprintStatus === 'verified' ? 'success' : remitterFingerprintStatus === 'capturing' ? 'warning' : 'primary'}
                        onClick={captureRemitterFingerprint}
                        disabled={loading || remitterFingerprintStatus === 'capturing'}
                        className="align-self-start"
                      >
                        {remitterFingerprintStatus === 'capturing' && <Spinner size="sm" className="me-2" />}
                        {remitterFingerprintStatus === 'verified' && <FaCheckCircle className="me-2" />}
                        {remitterFingerprintStatus === 'captured' && <FaFingerprint className="me-2" />}
                        {remitterFingerprintStatus === 'failed' && 'Retry'}
                        {remitterFingerprintStatus === 'idle' && <><FaFingerprint className="me-2" />Capture Fingerprint</>}
                        {remitterFingerprintStatus === 'captured' && 'Verifying...'}
                        {remitterFingerprintStatus === 'verified' && 'Captured ✓'}
                      </Button>
                    </div>
                    <Form.Text className="text-muted">
                      {remitterFingerprintStatus === 'idle' && 'Click to capture fingerprint from biometric device'}
                      {remitterFingerprintStatus === 'capturing' && 'Capturing fingerprint...'}
                      {remitterFingerprintStatus === 'captured' && 'Fingerprint captured, verifying...'}
                      {remitterFingerprintStatus === 'verified' && 'Fingerprint verified successfully ✓'}
                      {remitterFingerprintStatus === 'failed' && 'Fingerprint capture failed. Please try again.'}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Button 
                variant="success" 
                onClick={registerRemitter}
                disabled={loading || !remitterData.pid_data}
                className="w-100"
              >
                {loading ? <Spinner size="sm" /> : 'Register Remitter'}
              </Button>
            </div>
          </Tab>

          {/* Beneficiary Tab */}
          <Tab eventKey="beneficiary" title="Beneficiary">
            <div className="p-3">
              <h5><FaUserPlus className="me-2" />Beneficiary Management</h5>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Mobile Number *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 10-digit mobile number"
                      value={beneficiaryData.mobile}
                      onChange={(e) => setBeneficiaryData({...beneficiaryData, mobile: e.target.value})}
                      maxLength={10}
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="d-flex align-items-end">
                  <Button 
                    variant="info" 
                    onClick={getBeneficiaries}
                    disabled={loading}
                    className="w-100"
                  >
                    {loading ? <Spinner size="sm" /> : 'Get Beneficiaries'}
                  </Button>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Beneficiary Name *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter beneficiary name"
                      value={beneficiaryData.bene_name}
                      onChange={(e) => setBeneficiaryData({...beneficiaryData, bene_name: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Account Number *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter account number"
                      value={beneficiaryData.bene_account}
                      onChange={(e) => setBeneficiaryData({...beneficiaryData, bene_account: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>IFSC Code *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter IFSC code"
                      value={beneficiaryData.bene_ifsc}
                      onChange={(e) => setBeneficiaryData({...beneficiaryData, bene_ifsc: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Bank Name *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter bank name"
                      value={beneficiaryData.bene_bank}
                      onChange={(e) => setBeneficiaryData({...beneficiaryData, bene_bank: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Bank ID (numeric) *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter numeric bank ID (e.g. 364)"
                      value={beneficiaryData.bankid}
                      onChange={(e) => setBeneficiaryData({ ...beneficiaryData, bankid: e.target.value })}
                    />
                    <Form.Text className="text-muted">
                      PaySprint requires the bank's numeric ID from their master list.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Verification Status *</Form.Label>
                    <Form.Select
                      value={beneficiaryData.verified}
                      onChange={(e) => setBeneficiaryData({ ...beneficiaryData, verified: e.target.value })}
                    >
                      <option value="0">Unverified (0)</option>
                      <option value="1">Verified (1)</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Choose 1 only if the beneficiary has already been verified with PaySprint.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Button 
                variant="success" 
                onClick={registerBeneficiary}
                disabled={loading}
                className="w-100 mb-3"
              >
                {loading ? <Spinner size="sm" /> : 'Register Beneficiary'}
              </Button>

              {beneficiaries.length > 0 && (
                <div className="mt-3">
                  <h6>Beneficiary List</h6>
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Account</th>
                          <th>IFSC</th>
                          <th>Bank</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {beneficiaries.map((bene, index) => (
                          <tr key={index}>
                            <td>{bene.beneid || bene.bene_id || bene.id}</td>
                            <td>{bene.bene_name}</td>
                            <td>{bene.bene_account}</td>
                            <td>{bene.bene_ifsc}</td>
                            <td>{bene.bene_bank}</td>
                            <td>
                              <Button 
                                variant="info" 
                                size="sm" 
                                onClick={() => verifyBeneficiary(bene.beneid)}
                                disabled={loading}
                                className="me-2"
                              >
                                Verify
                              </Button>
                              <Button 
                                variant="danger" 
                                size="sm" 
                                onClick={() => deleteBeneficiary(bene.beneid)}
                                disabled={loading}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </Tab>

          {/* Transaction Tab */}
          <Tab eventKey="transaction" title="Transaction">
            <div className="p-3">
              <h5><FaExchangeAlt className="me-2" />Transaction Management</h5>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Mobile Number *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 10-digit mobile number"
                      value={transactionData.mobile}
                      onChange={(e) => setTransactionData({...transactionData, mobile: e.target.value})}
                      maxLength={10}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Beneficiary ID *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter beneficiary ID"
                      value={transactionData.beneid}
                      onChange={(e) => setTransactionData({...transactionData, beneid: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Amount *</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="Enter amount"
                      value={transactionData.amount}
                      onChange={(e) => setTransactionData({...transactionData, amount: e.target.value})}
                      min="1"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Reference ID *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Auto-generated if left blank"
                      value={transactionData.referenceid}
                      onChange={(e) => setTransactionData({...transactionData, referenceid: e.target.value})}
                    />
                    <Form.Text className="text-muted">
                      Must match the reference used when sending OTP.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Transaction Type *</Form.Label>
                    <Form.Select
                      value={transactionData.txntype}
                      onChange={(e) => setTransactionData({...transactionData, txntype: e.target.value})}
                    >
                      <option value="NEFT">NEFT</option>
                      <option value="IMPS">IMPS</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Latitude</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Latitude"
                      value={transactionData.latitude}
                      onChange={(e) => setTransactionData({...transactionData, latitude: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Longitude</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Longitude"
                      value={transactionData.longitude}
                      onChange={(e) => setTransactionData({...transactionData, longitude: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>OTP *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      value={transactionData.otp}
                      onChange={(e) => setTransactionData({...transactionData, otp: e.target.value})}
                      maxLength={6}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>State Response (stateresp)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Appears after sending OTP"
                      value={transactionData.stateresp}
                      readOnly
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex gap-2">
                <Button 
                  variant="primary" 
                  onClick={sendTransactionOtp}
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" /> : 'Send OTP'}
                </Button>
                <Button 
                  variant="success" 
                  onClick={processTransaction}
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" /> : 'Process Transaction'}
                </Button>
              </div>
            </div>
          </Tab>

          {/* Status Tab */}
          <Tab eventKey="status" title="Status">
            <div className="p-3">
              <h5><FaHistory className="me-2" />Transaction Status</h5>
              
              <Row className="mb-3">
                <Col md={8}>
                  <Form.Group>
                    <Form.Label>Transaction ID *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter transaction ID"
                      value={refundData.transactionid}
                      onChange={(e) => setRefundData({...refundData, transactionid: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={4} className="d-flex align-items-end">
                  <Button 
                    variant="info" 
                    onClick={checkTransactionStatus}
                    disabled={loading}
                    className="w-100"
                  >
                    {loading ? <Spinner size="sm" /> : 'Check Status'}
                  </Button>
                </Col>
              </Row>
            </div>
          </Tab>

          {/* Refund Tab */}
          <Tab eventKey="refund" title="Refund">
            <div className="p-3">
              <h5><FaUndo className="me-2" />Refund Management</h5>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Transaction ID *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter transaction ID"
                      value={refundData.transactionid}
                      onChange={(e) => setRefundData({...refundData, transactionid: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>OTP *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      value={refundData.otp}
                      onChange={(e) => setRefundData({...refundData, otp: e.target.value})}
                      maxLength={6}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex gap-2">
                <Button 
                  variant="warning" 
                  onClick={sendRefundOtp}
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" /> : 'Send Refund OTP'}
                </Button>
                <Button 
                  variant="danger" 
                  onClick={claimRefund}
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" /> : 'Claim Refund'}
                </Button>
              </div>
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DMTBank6Modal;
